import React from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

export interface SelectOption {
  value: string;
  label: string;
}

export interface FloatingLabelSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** @deprecated icon not supported in shadcn Select trigger */
  icon?: React.ReactNode;
  className?: string;
}

/**
 * FloatingLabelSelect — label-above-input pattern.
 * Built on shadcn / Radix UI Select:
 *   • Click-outside, ARIA, keyboard nav all handled by Radix
 *   • Portal rendering with automatic position management
 *   • Styled to match FloatingInput exactly (h-[40px], rounded-xl, border-slate-300)
 */
const FloatingLabelSelect: React.FC<FloatingLabelSelectProps> = ({
  label,
  value,
  onChange,
  options,
  required,
  error,
  disabled,
  className = '',
}) => {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>

      {/* ── Label ── */}
      <label
        className={cn(
          'block text-[11.5px] font-medium leading-none select-none',
          error ? 'text-red-500' : 'text-slate-500 dark:text-slate-400',
          disabled && 'opacity-50',
        )}
      >
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>

      {/* ── Radix Select ── */}
      <Select
        value={value || undefined}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={cn(
            // ── Match FloatingInput exactly ──
            'h-[40px] w-full rounded-xl border px-3.5',
            'bg-white dark:bg-surface-dark-300',
            'text-[13.5px] font-normal text-slate-800 dark:text-slate-100',
            'transition-all duration-150 outline-none ring-0',
            // ── Border / focus — matches FloatingInput ──
            error
              ? [
                  'border-red-300 dark:border-red-500/50',
                  'focus:border-red-400 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.10)]',
                  'hover:border-red-300',
                ]
              : [
                  'border-slate-300 dark:border-white/[0.18]',
                  'hover:border-brand-400/50 dark:hover:border-white/30',
                  'focus:border-brand-500 dark:focus:border-brand-400/60',
                  'focus:shadow-[0_0_0_3px_rgba(3,93,184,0.10)] dark:focus:shadow-[0_0_0_3px_rgba(43,141,224,0.15)]',
                ],
            // ── Override shadcn defaults that conflict ──
            'focus:ring-0 focus:ring-offset-0',
            'data-[placeholder]:text-slate-400',
          )}
        >
          <SelectValue placeholder="" />
        </SelectTrigger>

        <SelectContent className="max-h-72 overflow-y-auto">
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* ── Error ── */}
      {error && error.trim() && (
        <div className="flex items-center gap-1 mt-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FloatingLabelSelect;
